import { ClusterPhase, type Cluster } from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import type { DeepOmitProtobufInternals, MaskPaths } from '@/connect-query';
import { getValueAtPath } from '@/utils/cluster-utils';
import { type DeepPartial } from '@/utils/types';
import { isWithinSubtree, protobufSerializationReplacer } from '@/utils/utils';

type ClusterInput = DeepOmitProtobufInternals<Cluster>;

/**
 * A masked update only carries identity plus the masked fields, so the submitted
 * cluster is a partial one — every field below is read defensively.
 */
type PartialClusterInput = DeepPartial<ClusterInput>;

type ClusterMaskPath = MaskPaths<ClusterInput>;

const getStorageTierType = (cluster?: PartialClusterInput) =>
  cluster?.configuration?.clusterStorageConfiguration?.storageTierType;

/**
 * Detects configuration changes that should be represented as `SCALING` in the UI.
 */
export function hasScalingConfigurationChange(
  previousCluster: ClusterInput | undefined,
  nextCluster: PartialClusterInput,
) {
  if (!previousCluster) {
    return false;
  }

  const prevConfiguration = previousCluster.configuration;
  const nextConfiguration = nextCluster.configuration;

  return (
    prevConfiguration?.numberOfNodes !== nextConfiguration?.numberOfNodes ||
    prevConfiguration?.packageId !== nextConfiguration?.packageId ||
    prevConfiguration?.additionalResources?.disk !== nextConfiguration?.additionalResources?.disk ||
    getStorageTierType(previousCluster) !== getStorageTierType(nextCluster)
  );
}

const getStringifiedConfiguration = (cluster: PartialClusterInput) =>
  JSON.stringify(cluster.configuration ?? null, protobufSerializationReplacer);

/**
 * Generic configuration change detection (not necessarily scaling-related).
 *
 * We intentionally use serialization because this path only needs a fast signal
 * for optimistic phase selection, not a perfect semantic diff.
 */
export function hasConfigurationChange(previousCluster: ClusterInput | undefined, nextCluster: PartialClusterInput) {
  if (!previousCluster) {
    return false;
  }

  return getStringifiedConfiguration(previousCluster) !== getStringifiedConfiguration(nextCluster);
}

const CONFIGURATION_MASK_PATH = 'configuration' satisfies ClusterMaskPath;

const VERSION_MASK_PATH = 'configuration.version' satisfies ClusterMaskPath;

// Keep in sync with `hasScalingConfigurationChange`
const SCALING_MASK_PATHS = [
  'configuration.number_of_nodes',
  'configuration.package_id',
  'configuration.additional_resources.disk',
  'configuration.cluster_storage_configuration.storage_tier_type',
] satisfies ClusterMaskPath[];

/**
 * Mask-scoped phase selection for partial updates. A full-object diff would
 * compare real values with `undefined` (the payload only carries identity +
 * masked fields), but the masked fields themselves are always present, so the
 * mask tells us which values we can reliably compare against the cached cluster.
 */
function getMaskDrivenPhase(
  paths: readonly string[],
  previousCluster: ClusterInput | undefined,
  nextCluster: PartialClusterInput,
): ClusterPhase | undefined {
  if (!previousCluster) {
    return undefined;
  }

  const maskCovers = (fieldPath: string) => paths.some((path) => isWithinSubtree(fieldPath, path));

  // Serialized comparison, like `hasConfigurationChange`: a fast signal for
  // optimistic phase selection, not a perfect semantic diff.
  const valueChanged = (path: string) =>
    JSON.stringify(getValueAtPath(previousCluster, path), protobufSerializationReplacer) !==
    JSON.stringify(getValueAtPath(nextCluster, path), protobufSerializationReplacer);

  if (maskCovers(VERSION_MASK_PATH) && valueChanged(VERSION_MASK_PATH)) {
    return ClusterPhase.UPGRADING;
  }

  if (SCALING_MASK_PATHS.some((scalingPath) => maskCovers(scalingPath) && valueChanged(scalingPath))) {
    return ClusterPhase.SCALING;
  }

  if (paths.some((path) => isWithinSubtree(path, CONFIGURATION_MASK_PATH) && valueChanged(path))) {
    return ClusterPhase.UPDATING;
  }

  return undefined;
}

function getDiffDrivenPhase(
  previousCluster: ClusterInput | undefined,
  nextCluster: PartialClusterInput,
): ClusterPhase | undefined {
  if (!previousCluster) {
    return undefined;
  }

  const oldVersion = previousCluster.configuration?.version;
  const newVersion = nextCluster.configuration?.version;

  if (oldVersion !== newVersion) {
    return ClusterPhase.UPGRADING;
  }

  if (hasScalingConfigurationChange(previousCluster, nextCluster)) {
    return ClusterPhase.SCALING;
  }

  if (hasConfigurationChange(previousCluster, nextCluster)) {
    return ClusterPhase.UPDATING;
  }

  return undefined;
}

/**
 * Selects the optimistic phase shown by the UI right after "updateCluster" starts.
 *
 * Priority order (most specific first):
 * - 'UPGRADING' (version change)
 * - 'SCALING' (capacity/resource change)
 * - 'UPDATING' (non-scaling configuration change)
 *
 * When `updateMask` carries paths, only the masked fields are compared (the
 * submitted cluster is partial in that case).
 */
export function getOptimisticUpdatePhase(
  previousCluster: ClusterInput | undefined,
  nextCluster: PartialClusterInput,
  updateMask?: { paths: readonly string[] },
): ClusterPhase | undefined {
  if (updateMask && updateMask.paths.length > 0) {
    return getMaskDrivenPhase(updateMask.paths, previousCluster, nextCluster);
  }

  return getDiffDrivenPhase(previousCluster, nextCluster);
}
